import { QUESTIONS } from "../../constant";

const AUDIO_TYPES = [QUESTIONS.SPEECH, QUESTIONS.SPEECH_SCRIPT, QUESTIONS.SPEECH_SPONTANEOUS];
const WRITTEN_TYPES = [QUESTIONS.FREE_TEXT, QUESTIONS.TYPING_TEXT];
const TRAIT_SCORED_TYPES = [QUESTIONS.CHAT, QUESTIONS.FREE_TEXT, QUESTIONS.SPEECH_SPONTANEOUS];

export const QUESTION_TYPES = [
  { key: "mcq", name: "MCQ" },
  { key: "audio", name: "Audio" },
  { key: "written", name: "Written" },
  { key: "coding", name: "Coding" },
  { key: "chat", name: "Chat simulation" },
];

export const EMPTY_FILTERS = { skill: "", type: "", result: "" };

const isCorrect = (userScore, maxScore) => maxScore > 0 && (userScore ?? 0) >= maxScore;

/** A quiz question (BucketDetails' mcq list): its skills, its type and whether it scored full marks. */
export const describeQuiz = ({ question, answer, traits }) => {
  const type = AUDIO_TYPES.includes(question?.type)
    ? "audio"
    : WRITTEN_TYPES.includes(question?.type) ? "written" : "mcq";
  const userScore = TRAIT_SCORED_TYPES.includes(question?.type) ? answer?.traitScore : answer?.score?.userScore;
  return {
    skills: Array.from(traits ?? []).map(({ name }) => name),
    type,
    result: isCorrect(userScore, answer?.score?.maxScore) ? "correct" : "incorrect",
  };
};

/** A coding problem (BucketDetails' coding list). */
export const describeCoding = ({ score, traits }) => ({
  skills: Array.from(traits ?? []).map(({ name }) => name),
  type: "coding",
  result: isCorrect(score?.userScore, score?.maxScore) ? "correct" : "incorrect",
});

/** A chat simulation carries no per-skill split or full marks, so only the type filter narrows it. */
export const describeChat = () => ({ skills: [], type: "chat", result: null });

export const matches = (item, { skill, type, result }) =>
  (!skill || item.skills.includes(skill))
  && (!type || item.type === type)
  && (!result || item.result === result);

/**
 * The filters over a section's questions: by skill, by type and by result. A
 * type the section has none of is shown, and disabled.
 */
const QuestionFilters = ({ skills, items, filters, onChange }) => {
  const { skill, type, result } = filters;
  const total = items.length;
  const counts = items.reduce((acc, item) => ({ ...acc, [item.type]: (acc[item.type] ?? 0) + 1 }), {});
  const shown = items.filter((item) => matches(item, filters)).length;
  const any = skill || type || result;
  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <div className="r4-qfilters" role="group" aria-label="Filter the questions">
      <select value={skill} onChange={(e) => set({ skill: e.target.value })} aria-label="Skill" className="r4-qselect">
        <option value="">All skills</option>
        {skills.map((name) => <option key={name} value={name}>{name}</option>)}
      </select>
      <div className="r4-qseg" role="radiogroup" aria-label="Type">
        <button type="button" role="radio" aria-checked={!type} className={!type ? "r4-qseg--on" : undefined} onClick={() => set({ type: "" })}>
          All types
        </button>
        {QUESTION_TYPES.map(({ key, name }) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={type === key}
            className={type === key ? "r4-qseg--on" : undefined}
            disabled={!counts[key]}
            title={counts[key] ? undefined : "Not in this section"}
            onClick={() => set({ type: type === key ? "" : key })}
          >
            {name}
            {counts[key] ? <i>{counts[key]}</i> : null}
          </button>
        ))}
      </div>
      <div className="r4-qseg" role="radiogroup" aria-label="Result">
        {[["", "Any result"], ["correct", "Correct"], ["incorrect", "Incorrect"]].map(([key, name]) => (
          <button key={key || "any"} type="button" role="radio" aria-checked={result === key} className={result === key ? "r4-qseg--on" : undefined} onClick={() => set({ result: key })}>
            {name}
          </button>
        ))}
      </div>
      <span className="r4-qcount" aria-live="polite">{any ? `${shown} of ${total}` : `${total} questions`}</span>
      {any && <button type="button" className="r4-qclear" onClick={() => onChange(EMPTY_FILTERS)}>Clear</button>}
      {any && shown === 0 && <p className="r4-qnone">No question matches these filters.</p>}
    </div>
  );
};

export default QuestionFilters;
